// Third-party
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { withFormik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';

// Components
import { FormInput } from '../../components/forms';
import { PrimaryButton } from '../../components/buttons';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import PasswordCriteria from '../../components/errors/PasswordCriteria';
import notification from '../../../utils/notification';
import { passwordIsValid } from '../../../utils/auth';

type FormProps = {
  current: string;
  password: string;
  confirm: string;
};

type ParentProps = {
  onSave: (current: string, password: string, confirm: string) => Promise<void>;
};

type Props = ParentProps & RouteComponentProps & FormikProps<FormProps>;

const canSubmit = (props: Props) => {
  const { values, initialValues, isSubmitting, errors } = props;

  const isDirty = values !== initialValues;
  const hasErrors = Object.keys(errors).length > 0;

  return !hasErrors && isDirty && !isSubmitting;
};

const LegacySecurityPassword = (props: Props) => {
  const {
    values: { password, confirm },
  } = props;
  return (
    <div className="settings-security-password">
      <h3>
        <FormattedMessage id="Settings.Security.Password.Title" />
      </h3>

      <Form>
        <div className="fields">
          <FormInput name="current" type="password" label={formatMessage('Settings.Security.Password.Fields.Current')} required />
          <br />
          <FormInput name="password" type="password" label={formatMessage('Settings.Security.Password.Fields.Password')} required />
          <br />
          <FormInput name="confirm" type="password" label={formatMessage('Settings.Security.Password.Fields.Confirm')} required />
          <PasswordCriteria password={password} confirm={confirm} />
        </div>

        <PrimaryButton type="submit" disabled={!canSubmit(props)}>
          <FormattedMessage id="Common.Save" />
        </PrimaryButton>
      </Form>
    </div>
  );
};

const SettingsSecuritySchema = Yup.object().shape({
  current: Yup.string().required(),
  // The test message doesn't need to be translated - won't be shown anywhere.
  password: Yup.string().required().test('test-password', 'Password criteria unmet', passwordIsValid),
  confirm: Yup.string()
    .required()
    .oneOf([Yup.ref('password'), null]),
});

const formikWrapper = withFormik<Props, FormProps>({
  validationSchema: SettingsSecuritySchema,
  mapPropsToValues: props => ({ current: '', password: '', confirm: '' }),
  handleSubmit: ({ current, password, confirm }, { props, setSubmitting, resetForm }) => {
    props
      .onSave(current, password, confirm)
      .then(() => {
        notification.success(formatMessage('Settings.Security.Password.Success'));
        resetForm();
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(LegacySecurityPassword) as any;
